<script lang="ts" setup>
import OmniTabs from "@/components/omni/OmniTabs.vue";
import TripStatus from "@/views/trips/components/TripStatus.vue";
import NotFound from "@/views/notFound/NotFound.vue";
import SidebarLayout from "@/components/layout/SidebarLayout.vue";
import SidebarToggle from "@/components/buttons/SidebarToggle.vue";
import { useTripStore } from "@/store/trip.store";
import { computed, onMounted, ref } from "vue";
import { onBeforeRouteLeave, useRoute } from "vue-router";
import { useTrip } from "@/composables/usetTrip";
import type { TruckDto } from "@/api/trucks/dto/truck.dto";
import type { TripOverview } from "@/api/trips/dto/trip.dto";
import type { TripMonitorRoute } from "@/api/trips/dto/trip-monitor";
import PermissionTooltip from "@/components/tooltips/PermissionTooltip.vue";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto";
import { useI18n } from "vue-i18n";
import { useSocketEvent } from "@/composables/useSocketEvent";

const tripStore = useTripStore();
const route = useRoute();
const { createTripOverview } = useTrip();
const { t } = useI18n();

const loading = ref(true);

const editLink = computed(() => {
  return {
    name: "trip-edit",
    params: { id: route.params.id },
  };
});

const tripId = computed(() => {
  return Number(route.params.id);
});

const isSidebarOpenDefault = computed(() => {
  // let windowWidth = window.innerWidth;
  // return windowWidth > 1250;
  return false;
});

async function loadTripOverview() {
  if (!tripStore.tripOverview) {
    loading.value = true;
    const tripOverview = await createTripOverview(tripId.value);

    if (tripOverview) {
      tripStore.updateTripOverview(tripOverview);
    }
  }

  loading.value = false;
}

async function updateTripOverviewFunction() {
  const tripOverview = await createTripOverview(tripId.value);

  if (tripOverview) {
    tripStore.updateTripOverview(tripOverview);
  }
}

function updatedTripOverviewBySocketRoute(routeFromSocket: TripMonitorRoute) {
  if (!tripStore.tripOverview) {
    return;
  }

  const updatedTripOverview: TripOverview = {
    ...tripStore.tripOverview,
    routes: tripStore.tripOverview.routes.map((route) => {
      if (route.id === routeFromSocket?.id) {
        return {
          ...route,
          nextCheckCallTime: routeFromSocket.nextCheckCallTime,
          travelOrders: route.travelOrders.map((travelOrder) => {
            const relatedUpdatedTO = routeFromSocket.travelOrders.find((item) => item.id === travelOrder.id);

            if (relatedUpdatedTO) {
              return {
                ...travelOrder,
                nextUpdateTime: relatedUpdatedTO.nextUpdateTime,
              };
            }

            return travelOrder;
          }),
        };
      }

      return route;
    }),
  };

  tripStore.updateTripOverview(updatedTripOverview);
}

onMounted(async () => {
  await loadTripOverview();
});

useSocketEvent("trip:updated", async (payload, { claimLatest }) => {
  if (payload.tripId !== tripId.value) {
    return;
  }

  const isLatest = claimLatest();
  const tripOverview = await createTripOverview(tripId.value);

  if (!isLatest()) {
    return;
  }

  if (tripOverview) {
    tripStore.updateTripOverview(tripOverview);
  }
});

useSocketEvent("trip:truck-updated", ({ truck }) => {
  if (!tripStore.tripOverview) {
    return;
  }

  const updatedTrip = {
    ...tripStore.tripOverview,
    routes: tripStore.tripOverview.routes.map((route) => {
      if (route.truckId === truck.id && route.truck) {
        const newTruck: TruckDto = {
          ...route.truck,
          currentLocationTime: truck.currentLocationTime || undefined,
          currentLocation: truck.currentLocation || undefined,
          type: truck.type,
          number: truck.number,
        };

        return {
          ...route,
          truck: newTruck,
        };
      }

      return route;
    }),
  };

  tripStore.updateTripOverview(updatedTrip);
});

useSocketEvent("trip:route-updated", ({ route }) => {
  if (!tripStore.tripOverview) {
    return;
  }

  updatedTripOverviewBySocketRoute(route);
});

onBeforeRouteLeave(() => {
  tripStore.updateTripOverview(null);
});
</script>

<template>
  <div v-if="loading"></div>
  <v-container v-else-if="tripStore.tripOverview" :fluid="true" class="pt-6 pb-10 pl-6 pr-6 height-100 users-page">
    <sidebar-layout :sidebar-opened="isSidebarOpenDefault">
      <div class="w-100">
        <div class="d-flex justify-space-between align-center flex-wrap mb-4">
          <div class="d-flex align-center text-grey-darken-4 title font-weight-medium font-size-20">
            Trip #{{ tripStore.tripOverview?.id }}
            <trip-status :trip="tripStore.tripOverview" class="ml-4" data-qa="trip-status"></trip-status>
          </div>
          <div v-if="tripStore.tripOverview?.id" class="d-flex align-center">
            <permission-tooltip :permission="DispatcherPermissionValue.TripsEdit">
              <template #activator="{ disabled }">
                <v-btn
                  :disabled="disabled"
                  :to="editLink"
                  class="text-uppercase font-weight-600 text-white mr-2"
                  color="primary"
                  variant="flat"
                >
                  <v-icon class="mr-2" size="20"> mdi-pencil-outline</v-icon>
                  Manage trip
                </v-btn>
              </template>

              {{ t("permission.manage-trip") }}
            </permission-tooltip>
            <sidebar-toggle class="mr-2"></sidebar-toggle>
          </div>
        </div>

        <v-card v-if="tripStore.tripOverview" class="bg-transparent" variant="flat">
          <omni-tabs :model-value="tripStore.tripOverviewTab" class="rounded">
            <v-tab
              :to="{ name: 'trip-view', params: { id: tripStore.tripOverview?.id } }"
              class="text-uppercase"
              value="trip-view"
              @click="tripStore.updateTripOverviewTab('trip-view')"
              >Overview
            </v-tab>

            <permission-tooltip :permission="DispatcherPermissionValue.CheckCallsList">
              <template #activator="{ disabled }">
                <v-tab
                  v-if="tripStore.tripOverview.routes"
                  :disabled="disabled"
                  :to="{ name: 'trip-check-calls', params: { id: tripStore.tripOverview?.id } }"
                  class="text-uppercase ml-0"
                  value="trip-check-calls"
                  @click="tripStore.updateTripOverviewTab('trip-check-calls')"
                >
                  Check calls
                </v-tab>
              </template>

              {{ t("permission.view-check-call") }}
            </permission-tooltip>

            <permission-tooltip :permission="DispatcherPermissionValue.BrokerUpdatesList">
              <template #activator="{ disabled }">
                <v-tab
                  v-if="tripStore.tripOverview.routes"
                  :disabled="disabled"
                  :to="{ name: 'trip-broker-updates', params: { id: tripStore.tripOverview?.id } }"
                  class="text-uppercase ml-0"
                  value="trip-broker-updates-order"
                  @click="tripStore.updateTripOverviewTab('trip-broker-updates-order')"
                >
                  Updates
                </v-tab>
              </template>

              {{ t("permission.view-broker-update") }}
            </permission-tooltip>
          </omni-tabs>

          <router-view v-slot="{ Component }">
            <component :is="Component" :trip="tripStore.tripOverview" @change="updateTripOverviewFunction" />
          </router-view>
        </v-card>
      </div>
    </sidebar-layout>
  </v-container>
  <not-found v-else icon="mdi-account-remove-outline" text="Trip not found"></not-found>
</template>

<style lang="scss" scoped>
.title {
  height: 36px;
}
</style>
